import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, ConfigProvider, Popconfirm, Spin, message } from 'antd';
import {
    CloseOutlined,
    CaretLeftOutlined,
    CaretRightOutlined,
    ArrowLeftOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReplaySidebar from './components/ReplaySidebar';
import ReplayMap from './components/ReplayMap';
import ManualAdjustPanel from './components/ManualAdjustPanel';
import { useReplayRoute } from './hooks/useReplayRoute';
import { useManualReorder } from './hooks/useManualReorder';
import { useReplayMetrics } from './hooks/useReplayMetrics';
import { ReplayStop, ReplayMetricsFields } from './graphql/types';
import { ReplayMetrics } from './graphql/computeReplayMetrics';
import { ReplayManualVersion } from './graphql/listReplayManualVersions';
import { METRIC_SPECS, MetricSpec, compare, Diff, fmtKm, fmtDur } from './utils/metricsFormat';
import { REPLAY_PRIMARY } from './theme';

const middleStops = (stops: ReplayStop[]) => stops.filter((s) => s.stop !== '-1' && s.stop !== '-2');
const sameOrder = (a: ReplayStop[], b: ReplayStop[]) => a.length === b.length && a.every((s, i) => s.stop === b[i].stop);

const toneClass: Record<Diff['tone'], string> = {
    better: 'text-green-600',
    worse: 'text-red-500',
    even: 'text-gray-400',
    na: 'text-gray-400',
};

// 策略对比表各指标列宽
const COLW: Record<string, string> = {
    totalDistanceKm: 'w-12',
    totalDurationMinutes: 'w-14',
    leftTurnCount: 'w-8',
    uTurnCount: 'w-8',
    trafficLightCount: 'w-11',
};

// 表内距离只显示数值（列名已表明单位）
const cellValue = (spec: MetricSpec, m: ReplayMetricsFields | null) =>
    spec.key === 'totalDistanceKm'
        ? m?.totalDistanceKm != null
            ? m.totalDistanceKm.toFixed(1)
            : '—'
        : spec.fmt(m?.[spec.key]);

const label = (t: (k: string) => string, spec: MetricSpec) => t(`delivery.routeReplay.metric.${spec.labelKey}`);

/** 有符号差值文案：-2.3 km / +3 / 持平 */
function DeltaText({ spec, diff }: { spec: MetricSpec; diff: Diff }) {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;
    if (diff.tone === 'na') return <span className="text-gray-300">—</span>;
    if (diff.tone === 'even') return <span className={toneClass.even}>{t('delivery.routeReplay.diff.even')}</span>;
    const sign = diff.delta! < 0 ? '-' : '+';
    return <span className={toneClass[diff.tone]}>{`${sign}${spec.fmt(Math.abs(diff.delta!))}`}</span>;
}

/** 本次结果对比表：5 指标 × [算法 | 手动 | 差值] */
const ResultCompare: React.FC<{ algorithm: ReplayMetricsFields | null; manual: ReplayMetricsFields | null }> = ({
    algorithm,
    manual,
}) => {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;
    return (
        <div className="rounded-xl border border-gray-100 overflow-hidden text-sm">
            <div className="grid grid-cols-[1fr_auto_auto_auto] bg-gray-50 text-[11px] text-gray-400 px-3 py-1.5 gap-x-3">
                <span />
                <span className="text-right w-20 whitespace-nowrap">{t('delivery.routeReplay.result.colAlgorithm')}</span>
                <span className="text-right w-20 whitespace-nowrap">{t('delivery.routeReplay.result.colManual')}</span>
                <span className="text-right w-20 whitespace-nowrap">{t('delivery.routeReplay.result.colDiff')}</span>
            </div>
            {METRIC_SPECS.map((s) => (
                <div key={s.key} className="grid grid-cols-[1fr_auto_auto_auto] items-center px-3 py-2 gap-x-3 border-t border-gray-50">
                    <span className="text-gray-600 whitespace-nowrap">{label(t, s)}</span>
                    <span className="text-right w-20 whitespace-nowrap text-gray-500">{s.fmt(algorithm?.[s.key])}</span>
                    <span className="text-right w-20 whitespace-nowrap font-semibold text-gray-800">{s.fmt(manual?.[s.key])}</span>
                    <span className="text-right w-20 whitespace-nowrap text-xs">
                        <DeltaText spec={s} diff={compare(manual?.[s.key], algorithm?.[s.key])} />
                    </span>
                </div>
            ))}
        </div>
    );
};

/** 策略对比表：版本 × [距离/时间/左转/掉头/红灯]，算法行 + 已保存版本（点选载入） */
const VersionCompareList: React.FC<{
    algorithm: ReplayMetricsFields | null;
    versions: ReplayManualVersion[];
    onLoad?: (v: ReplayManualVersion) => void;
    onDelete?: (v: ReplayManualVersion) => void;
    selectedId?: number | null;
}> = ({ algorithm, versions, onLoad, onDelete, selectedId }) => {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;

    const headerCells = METRIC_SPECS.map((s) => (
        <div key={s.key} className={`${COLW[s.key]} shrink-0 text-right text-[10px] leading-tight text-gray-400 whitespace-nowrap`}>
            {t(`delivery.routeReplay.metricShort.${s.labelKey}`)}
        </div>
    ));

    const metricCells = (m: ReplayMetricsFields | null) =>
        METRIC_SPECS.map((s) => (
            <div key={s.key} className={`${COLW[s.key]} shrink-0 text-right text-xs leading-tight tabular-nums text-gray-700 whitespace-nowrap`}>
                {cellValue(s, m)}
            </div>
        ));

    return (
        <div className="text-sm">
            {/* 表头 */}
            <div className="flex items-end gap-x-0.5 px-2 pb-2.5 border-b border-gray-100">
                <div className="flex-1 min-w-[90px] text-[11px] text-gray-400">{t('delivery.routeReplay.compare.colVersion')}</div>
                {headerCells}
            </div>

            {/* 算法行（浅蓝高亮） */}
            <div className="flex items-center gap-x-0.5 px-2 py-2.5 my-3 rounded-lg bg-[#eef2ff]">
                <div className="flex-1 min-w-[90px] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: REPLAY_PRIMARY }} />
                    <span className="font-semibold text-gray-800 whitespace-nowrap">{t('delivery.routeReplay.compare.algorithmRow')}</span>
                </div>
                {metricCells(algorithm)}
            </div>

            {/* 已保存版本区 */}
            <div className="px-2 py-2 mt-2 bg-gray-50 text-[11px] text-gray-400 rounded">{t('delivery.routeReplay.compare.savedBand')}</div>

            {versions.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-4">{t('delivery.routeReplay.compare.empty')}</div>
            ) : (
                <>
                    {versions.map((v) => (
                        <div key={v.id} className="relative mt-3">
                            <button
                                className={`w-full flex items-center gap-x-0.5 px-2 py-2.5 text-left rounded-lg disabled:cursor-default ${
                                    selectedId === v.id
                                        ? 'bg-orange-100'
                                        : 'bg-orange-50 hover:bg-orange-100 disabled:hover:bg-orange-50'
                                }`}
                                onClick={() => onLoad?.(v)}
                                disabled={!onLoad}
                            >
                                <div className="flex-1 min-w-[90px]">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                                        <span className="font-semibold text-orange-600 whitespace-nowrap">
                                            {t('delivery.routeReplay.compare.versionRow', { no: v.versionNo })}
                                        </span>
                                    </div>
                                    <div className="pl-4 mt-1.5 space-y-1 leading-tight">
                                        {v.savedByName && <div className="text-[11px] text-gray-400 truncate">{v.savedByName}</div>}
                                        <div className="text-[11px] text-gray-400">{dayjs(v.savedAt).format('M/D HH:mm')}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-x-0.5 mt-2">{metricCells(v)}</div>
                            </button>
                            {onDelete && (
                                <Popconfirm
                                    title={t('delivery.routeReplay.manual.deleteConfirm')}
                                    okText={t('delivery.routeReplay.manual.deleteOk')}
                                    cancelText={t('delivery.routeReplay.manual.deleteCancel')}
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => onDelete(v)}
                                >
                                    <button
                                        className="absolute top-1.5 right-1.5 p-1 rounded text-orange-500 hover:text-orange-600 hover:bg-orange-100"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <DeleteOutlined />
                                    </button>
                                </Popconfirm>
                            )}
                        </div>
                    ))}
                    <div className="text-center text-[11px] text-gray-400 pt-3 px-2">{t('delivery.routeReplay.compare.footer')}</div>
                </>
            )}
        </div>
    );
};

interface RoutePlanningReplayPageProps {
    parentDspTaskId: number;
    areaName?: string;
    onClose: () => void;
}

const EMPTY_STOPS: ReplayStop[] = [];

const RoutePlanningReplayPage: React.FC<RoutePlanningReplayPageProps> = ({ parentDspTaskId, areaName, onClose }) => {
    const { t: t0 } = useTranslation();
    const t = t0 as (key: string, options?: Record<string, unknown>) => string;
    const [messageApi, messageCtx] = message.useMessage();
    // 嵌入式：G3 下拉选路 + G4 详情
    const replay  = useReplayRoute(parentDspTaskId);
    // 指标编排
    const metrics = useReplayMetrics(replay.detail);
    // 面板收起（地图专注）
    const [collapsed, setCollapsed] = useState(false);
    // 手动调整模式
    const [manualMode, setManualMode] = useState(false);
    // 收起态「查看完整对比」卡片
    const [showFullCompare, setShowFullCompare] = useState(false);
    // 手动「查看本次结果」弹窗 + 本次手动指标
    const [showResult, setShowResult] = useState(false);
    const [manualMetrics, setManualMetrics] = useState<ReplayMetrics | null>(null);
    const [computingResult, setComputingResult] = useState(false);
    // 载入已保存版本
    const [seedOrder, setSeedOrder] = useState<ReplayStop[] | null>(null);
    const [loadedVersionId, setLoadedVersionId] = useState<number | null>(null);

    // 换线路：一切重置回算法初态（退出手动、清载入版本/本次指标/浮层卡）
    useEffect(() => {
        setManualMode(false);
        setShowResult(false);
        setShowFullCompare(false);
        setManualMetrics(null);
        setSeedOrder(null);
        setLoadedVersionId(null);
    }, [replay.selectedDspTaskId]);

    // 手动重排（单点 / 区间）；载入版本时用 seedOrder 作为初始顺序
    const reorder = useManualReorder(replay.detail?.stops ?? EMPTY_STOPS, manualMode, seedOrder);
    const displayStops = manualMode ? reorder.order : replay.detail?.stops ?? EMPTY_STOPS;
    const anchorNo = reorder.anchorKey ? reorder.order.findIndex((s) => s.stop === reorder.anchorKey) + 1 : 0;

    // 顶部操作提示
    let banner: string | null = null;
    if (manualMode) {
        if (reorder.mode === 'single' && reorder.anchorKey) {
            banner = t('delivery.routeReplay.banner.singleAnchored', { no: anchorNo });
        } else if (reorder.mode === 'range') {
            if (reorder.rangePhase === 'pickStart') {
                banner = t('delivery.routeReplay.banner.rangePickStart');
            } else if (reorder.rangePhase === 'pickEnd') {
                banner = t('delivery.routeReplay.banner.rangePickEnd', { no: reorder.rangeInfo?.loNo });
            } else if (reorder.rangePhase === 'pickDest') {
                banner = t('delivery.routeReplay.banner.rangePickDest', {
                    lo: reorder.rangeInfo?.loNo,
                    hi: reorder.rangeInfo?.hiNo,
                    count: reorder.rangeInfo?.count,
                });
            }
        }
    }

    // 底部落位 toast
    const lm = reorder.lastMove;
    let lastMoveText: string | null = null;
    if (lm?.type === 'single') {
        lastMoveText = t('delivery.routeReplay.toast.single', { from: lm.fromNo, to: lm.toNo });
    } else if (lm?.type === 'range') {
        lastMoveText = t('delivery.routeReplay.toast.range', {
            lo: lm.loNo,
            hi: lm.hiNo,
            count: lm.count,
            dest: lm.destNo,
            newStart: lm.newStartNo,
            newEnd: lm.newEndNo,
        });
    }

    // 左栏可见：未收起即可（手动模式下左右两栏并存）
    const sidebarVisible = !collapsed;

    const enterManual = () => {
        setSeedOrder(null);
        setLoadedVersionId(null);
        setManualMetrics(null);
        setManualMode(true);
        setShowFullCompare(false);
        setCollapsed(true); // 进手动模式收起左栏，地图专注调整
    };
    const exitManual = () => {
        setManualMode(false);
        setShowResult(false);
        setManualMetrics(null);
        setSeedOrder(null);
        setLoadedVersionId(null);
        setCollapsed(false); // 退出手动恢复左栏
    };

    // 删除某已保存版本（逻辑删）：删的若是当前载入的版本，删完退出手动模式回到详情
    const handleDeleteVersion = async (v: ReplayManualVersion) => {
        const ok = await metrics.deleteManual(v.id);
        if (ok && loadedVersionId === v.id) exitManual();
    };

    // 点策略对比里的版本 → 载入其顺序进手动模式，直接展示其已存指标
    const loadVersion = (v: ReplayManualVersion) => {
        setSeedOrder(middleStops(v.stops));
        setLoadedVersionId(v.id);
        setManualMetrics({
            totalDistanceKm: v.totalDistanceKm,
            totalDurationMinutes: v.totalDurationMinutes,
            leftTurnCount: v.leftTurnCount,
            uTurnCount: v.uTurnCount,
            trafficLightCount: v.trafficLightCount,
        });
        setShowFullCompare(false);
        setManualMode(true);
    };

    // 「查看本次结果」：按当前重排顺序算指标（G5），弹出对比
    const handleViewResult = async () => {
        setShowResult(true);
        setManualMetrics(null);
        setComputingResult(true);
        setCollapsed(false); // 查看本次结果时展开左栏，展示与算法的对比值
        try {
            const m = await metrics.computeManual(reorder.order);
            setManualMetrics(m);
        } finally {
            setComputingResult(false);
        }
    };

    // 保存本次手动版本（G6），成功后刷新对比列表（G7）
    const handleSaveResult = async () => {
        if (!manualMetrics) return;
        try {
            const ok = await metrics.saveManual(reorder.order, manualMetrics);
            if (ok) {
                messageApi.success(t('delivery.routeReplay.result.saved'));
                setShowResult(false);
            } else {
                messageApi.error(t('delivery.routeReplay.result.saveFailed'));
            }
        } catch (_e) {
            messageApi.error(t('delivery.routeReplay.result.saveFailed'));
        }
    };

    // 切换 单点/区间 tab：顺序复位（hook 内）+ 清掉本次计算指标（载入版本除外，其顺序=版本，指标仍匹配）
    const handleModeChange = (m: 'single' | 'range') => {
        reorder.setMode(m);
        if (loadedVersionId == null) setManualMetrics(null);
    };

    // 进行中是否有选择（ESC 逻辑用）：单点看锚点，区间看是否已开始选段
    const hasSelection = reorder.mode === 'single' ? !!reorder.anchorKey : reorder.rangePhase !== 'pickStart';

    // ESC 全局：查看模式 → 退出页面；调整模式：有进行中的选择 → 结束选择，否则 → 退出编辑模式
    // 注：点地图空白不触发任何操作，取消/退出只能靠 ESC 或「退出」按钮
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (!manualMode) {
                onClose();
            } else if (hasSelection) {
                reorder.cancelSelection();
            } else {
                exitManual();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualMode, hasSelection]);

    return (
        <ConfigProvider
            theme={{
                token: { colorPrimary: REPLAY_PRIMARY, borderRadiusLG: 8 },
                components: { Segmented: { itemSelectedBg: REPLAY_PRIMARY, itemSelectedColor: '#ffffff' } },
            }}
        >
            <div className="flex flex-col h-full min-h-0 bg-white">
                {messageCtx}
                {/* ===== 顶部信息栏（与 SplitRouteHistoryDrawer 对齐：白底、py-5、返回 + 竖线标题）===== */}
                <div className="flex items-center py-5 px-4 border-b border-gray-200 bg-white shrink-0">
                    <Button
                        type="text"
                        onClick={onClose}
                        style={{color: REPLAY_PRIMARY, padding: '0 4px'}}
                        icon={
                            <ArrowLeftOutlined style={{
                                fontSize: 22,
                                color: REPLAY_PRIMARY,
                                filter: `drop-shadow(0 0 0.5px ${REPLAY_PRIMARY}) drop-shadow(0 0 0.5px ${REPLAY_PRIMARY})`,
                            }}/>
                        }
                    />
                    <div className="border-l-4 pl-2 ml-1 flex items-baseline gap-2 text-xl font-bold" style={{ borderColor: REPLAY_PRIMARY }}>
                        <span>{t('delivery.routeReplay.title')}</span>
                        <span>{areaName ?? ''}</span>
                    </div>
                    <div className="ml-auto pr-8">
                        <Button onClick={onClose}>{t('delivery.routeReplay.topbar.back')}</Button>
                    </div>
                </div>

                {/* ===== 主体 ===== */}
                <div className="flex-1 flex min-h-0">
                    {/* 左栏 */}
                    {sidebarVisible && (
                        <ReplaySidebar
                            routes={replay.routes}
                            selectedDspTaskId={replay.selectedDspTaskId}
                            onSelectRoute={replay.selectRoute}
                            areaName={areaName}
                            detail={replay.detail}
                            algorithmMetrics={metrics.algorithmMetrics}
                            algorithmLoading={metrics.algorithmLoading}
                            savedVersions={metrics.savedVersions}
                            manualMode={manualMode}
                            manualMetrics={manualMetrics}
                            loadedVersionId={loadedVersionId}
                            onLoadVersion={loadVersion}
                            onDeleteVersion={handleDeleteVersion}
                            onEnterManual={enterManual}
                        />
                    )}

                    {/* 地图区 */}
                    <div className="relative flex-1 min-w-0">
                        <ReplayMap
                            stops={displayStops}
                            startPoint={replay.detail?.startPoint ?? null}
                            endPoint={replay.detail?.endPoint ?? null}
                            areaId={replay.detail?.route?.areaId ?? undefined}
                            fitToken={replay.detail?.route?.dspTaskId ?? null}
                            fetchTrajectory={(order) =>
                                metrics.getTrajectory(
                                    order,
                                    loadedVersionId != null && seedOrder && sameOrder(order, seedOrder) ? loadedVersionId : null
                                )
                            }
                            manualMode={manualMode}
                            adjustMode={reorder.mode}
                            anchorKey={reorder.anchorKey}
                            rangeKeys={reorder.rangeKeys}
                            rangePhase={reorder.rangePhase}
                            onStopClick={reorder.clickStop}
                        />

                        {/* 顶部操作提示 */}
                        {banner && (
                            <div
                                className="absolute left-1/2 top-3 -translate-x-1/2 z-20 rounded-lg bg-orange-500 text-white text-xs px-3 py-1.5 shadow-lg whitespace-nowrap">
                                {banner}
                            </div>
                        )}

                        {/* 底部落位 toast */}
                        {manualMode && lastMoveText && (
                            <div
                                className="absolute left-1/2 bottom-4 -translate-x-1/2 z-20 flex items-center gap-3 rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-lg whitespace-nowrap">
                                <span>{lastMoveText}</span>
                                <button className="text-purple-300 hover:text-purple-200" onClick={reorder.undo}>
                                    {t('delivery.routeReplay.toast.undo')}
                                </button>
                            </div>
                        )}

                        {/* 左上浮层 pill */}
                        <div className="absolute left-3 top-3 z-10">
                            {sidebarVisible ? (
                                <button
                                    onClick={() => setCollapsed(true)}
                                    className="flex items-center gap-1.5 rounded-xl bg-gray-100 shadow border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-200"
                                >
                                    <CaretLeftOutlined className="text-[10px] text-gray-500"/>
                                    {t('delivery.routeReplay.pill.collapse')}
                                </button>
                            ) : (
                                <div
                                    className="flex items-center gap-2 rounded-xl bg-white shadow border border-gray-200 px-2 py-1.5">
                                    <button
                                        onClick={() => setCollapsed(false)}
                                        className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50"
                                    >
                                        <CaretRightOutlined
                                            className="text-xs text-gray-500"/> {t('delivery.routeReplay.pill.expand')}
                                    </button>
                                    {manualMode ? (
                                        manualMetrics ? (
                                            <span className="text-sm font-semibold text-gray-800">
                                            {fmtKm(manualMetrics.totalDistanceKm)} · {fmtDur(manualMetrics.totalDurationMinutes)}
                                        </span>
                                        ) : (
                                            <span className="text-sm text-gray-500">
                                            {t('delivery.routeReplay.pill.algorithm')} {fmtKm(metrics.algorithmMetrics?.totalDistanceKm)} · {fmtDur(metrics.algorithmMetrics?.totalDurationMinutes)} · {t('delivery.routeReplay.pill.computeHint')}
                                        </span>
                                        )
                                    ) : (
                                        <>
                                            <span
                                                className="rounded-md border border-gray-200 bg-white text-gray-700 text-xs px-2 py-1">{t('delivery.routeReplay.pill.algorithm')}</span>
                                            <span className="text-sm font-semibold text-gray-800">
                                            {metrics.algorithmLoading && !metrics.algorithmMetrics ? (
                                                <Spin size="small"/>
                                            ) : (
                                                `${fmtKm(metrics.algorithmMetrics?.totalDistanceKm)} · ${fmtDur(metrics.algorithmMetrics?.totalDurationMinutes)}`
                                            )}
                                        </span>
                                            <button
                                                onClick={() => setShowFullCompare((v) => !v)}
                                                className="rounded-md border font-medium text-xs px-2 py-1 hover:bg-purple-50"
                                                style={{ borderColor: REPLAY_PRIMARY, color: REPLAY_PRIMARY }}
                                            >
                                                {t('delivery.routeReplay.pill.viewFullCompare')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 完整对比卡片（收起态） */}
                        {showFullCompare && !manualMode && (
                            <Card
                                size="small"
                                title={t('delivery.routeReplay.compare.title')}
                                className="absolute left-3 top-14 z-10 w-96 shadow-xl"
                                extra={<Button type="text" size="small" icon={<CloseOutlined/>}
                                               onClick={() => setShowFullCompare(false)}/>}
                            >
                                <VersionCompareList algorithm={metrics.algorithmMetrics}
                                                    versions={metrics.savedVersions} onLoad={loadVersion}
                                                    onDelete={handleDeleteVersion}
                                                    selectedId={loadedVersionId}/>
                            </Card>
                        )}

                        {/* 本次计算结果弹窗（浮层卡片，不遮全屏） */}
                        {showResult && (
                            <Card
                                size="small"
                                title={t('delivery.routeReplay.result.title')}
                                className="absolute left-1/2 top-16 -translate-x-1/2 z-20 w-[460px] shadow-2xl"
                                extra={<Button type="text" size="small" icon={<CloseOutlined/>}
                                               onClick={() => setShowResult(false)}/>}
                            >
                                {computingResult ? (
                                    <div className="h-28 flex items-center justify-center text-xs text-gray-400">
                                        <Spin size="small"
                                              className="mr-2"/> {t('delivery.routeReplay.result.computing')}
                                    </div>
                                ) : (
                                    <ResultCompare algorithm={metrics.algorithmMetrics} manual={manualMetrics}/>
                                )}
                                <Button
                                    block
                                    type="primary"
                                    className="mt-3"
                                    loading={metrics.saving}
                                    disabled={computingResult || !manualMetrics}
                                    onClick={handleSaveResult}
                                    style={{height: 44, fontWeight: 600, background: '#16a34a', borderColor: '#16a34a'}}
                                >
                                    {t('delivery.routeReplay.result.save')}
                                </Button>
                            </Card>
                        )}

                        {/* 手动调整面板 —— 悬浮在地图右侧 */}
                        {manualMode && (
                            <div className="absolute right-3 top-3 bottom-3 z-10">
                                <ManualAdjustPanel
                                    order={reorder.order}
                                    startPoint={replay.detail?.startPoint ?? null}
                                    mode={reorder.mode}
                                    onModeChange={handleModeChange}
                                    anchorKey={reorder.anchorKey}
                                    rangeKeys={reorder.rangeKeys}
                                    focus={reorder.focus}
                                    canUndo={reorder.canUndo}
                                    canRedo={reorder.canRedo}
                                    onUndo={reorder.undo}
                                    onRedo={reorder.redo}
                                    onReset={reorder.reset}
                                    onExit={exitManual}
                                    onViewResult={handleViewResult}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default RoutePlanningReplayPage;
